import { z } from 'zod';
import { MOWER_IDS, type MowerId } from '../../content/mowers';
import { TOOLS, type ToolId } from '../../content/tools';
import { CustomLawnSchema } from './customLawnSchema';
import type { PostedRun } from '../online/run';
import type { PlayerName } from '../online/schema';
import { BOOST_IDS, type BoostId } from '../../sim/boosts';

/**
 * Save document, version 1 (docs/ARCHITECTURE.md §8). One JSON blob holding all progress.
 * Changing its shape means bumping the version and adding a migration in ./migrate.ts.
 */
export const SAVE_VERSION = 2;

const MowerIdSchema = z.enum(MOWER_IDS as [MowerId, ...MowerId[]]);
const ToolIdSchema = z.enum(TOOLS.map((t) => t.id) as [ToolId, ...ToolId[]]);
const BoostIdSchema = z.enum(BOOST_IDS as [BoostId, ...BoostId[]]);

/** Star bits earned on a lawn. */
/**
 * Lawn record bits. `clear` marks the lawn as mowed (not a star); the three stars are par (or a
 * boss's own goal), all coins, and the lawn's challenge (src/content/challenges.ts).
 */
export const STAR = { clear: 1, par: 2, allCoins: 4, challenge: 8 } as const;
/** The three stars, in display order. */
export const STAR_BITS = [STAR.par, STAR.allCoins, STAR.challenge] as const;

export const LevelRecordSchema = z.object({
  /** Bitmask of {@link STAR}. */
  stars: z.number().int().min(0).max(15),
  bestTicks: z.number().int().positive().optional(),
});
export type LevelRecord = z.infer<typeof LevelRecordSchema>;

export const UpgradeLevelsSchema = z.object({
  tank: z.number().int().min(0).default(0),
  hp: z.number().int().min(0).default(0),
  /** Sharpened Blades: +1 bite per level (tall grass takes one pass fewer). */
  blades: z.number().int().min(0).default(0),
  /** Tuned Engine: a little faster per level. */
  engine: z.number().int().min(0).default(0),
  /**
   * Tuned Engine levels in use, when the player has dialed it back (it stays bought, and can be
   * turned back up). Absent: every level bought is in use.
   */
  engineInUse: z.number().int().min(0).optional(),
  /** Power Steering: eases the turn rule (reel pull-back recharge, tractor turning radius). */
  steering: z.number().int().min(0).default(0),
  /** Reverse Gear: a 180 for mowers that can't otherwise reverse. */
  reverse: z.number().int().min(0).default(0),
  /** Hardened Tires: one puncture shrugged off per lawn, per level. */
  tires: z.number().int().min(0).default(0),
  /** Bumper: one crash per lawn costs no hit, per level. */
  bumper: z.number().int().min(0).default(0),
  /** Reserve Can: a dry tank fills up again by itself, once a lawn. */
  reserve: z.number().int().min(0).default(0),
});
export type UpgradeLevels = z.infer<typeof UpgradeLevelsSchema>;

export const SaveSchema = z.object({
  version: z.literal(SAVE_VERSION),
  coins: z.number().int().min(0),
  /** Per-lawn results, keyed by level id. */
  levels: z.record(z.string(), LevelRecordSchema),
  ownedMowers: z.array(MowerIdSchema).min(1),
  selectedMower: MowerIdSchema,
  upgrades: z.partialRecord(MowerIdSchema, UpgradeLevelsSchema),
  /** One-use boosts in stock, by kind. */
  boosts: z.partialRecord(BoostIdSchema, z.number().int().min(0)).default({}),
  tools: z.object({
    owned: z.array(ToolIdSchema),
    levels: z.partialRecord(ToolIdSchema, z.number().int().min(0)),
    /** Loadout in priority order; a mower carries as many as it has slots. */
    equipped: z.array(ToolIdSchema).default([]),
  }),
  /** Rare finds kept (item ids). */
  rareFinds: z.array(z.string()),
  /** Lawns whose own rare find got away (level ids): it isn't there any more. */
  rareEscaped: z.array(z.string()).default([]),
  /**
   * Rare finds that got away and wander the campaign: each turns up on the next lawn after `from`
   * that is still to clear (see src/game/rareFinds.ts).
   */
  rareWandering: z.array(z.object({ item: z.string(), from: z.number().int() })).default([]),
  /** Campaign zones (biome ids) the player has looked at: new ones get a banner and a nudge. */
  zonesVisited: z.array(z.string()).default([]),
  cosmetics: z.object({ owned: z.array(z.string()), equipped: z.record(z.string(), z.string()) }),
  /** Zones whose All-Stars reward has been given (each only once). */
  zoneRewards: z.array(z.string()).default([]),
  /** Milestones already cheered (meta/reveals); absent on a save from before they existed. */
  milestones: z.array(z.string()).optional(),
  /** Unlocks looked at in the Garage (its NEW badge); absent on a save from before it. */
  garageSeen: z.array(z.string()).optional(),
  /** One-time hints already shown (`itemBox`: how to fire an item box's item). */
  tipsSeen: z.array(z.string()).optional(),
  /** Campaign lawns (level ids) whose secret yard was found and its first haul banked. */
  secretYards: z.array(z.string()).default([]),
  /** The lawn builder's lawns (meta/customLawns), newest first. */
  customLawns: z.array(CustomLawnSchema).default([]),
  /**
   * The full game unlocked (the one-time purchase, docs/MONETIZATION.md): campaign lawns past the
   * free ones. Mirrors the store's record; the store is the authority (restore, codes).
   */
  fullGame: z.boolean().default(false),
  /** The lawn builder bought (a coin unlock), and the piece kits bought for it (zone ids). */
  builder: z.object({ owned: z.boolean(), kits: z.array(z.string()) }).default({
    owned: false,
    kits: [],
  }),
  /** Times the game was shared with a friend (Settings > TELL A FRIEND; meta/sharing). */
  shares: z.number().int().min(0).default(0),
  /** Mowers taken on a lawn at least once: the card before a lawn introduces a new one. */
  mowersDriven: z.array(MowerIdSchema).default([]),
  settings: z.object({
    haptics: z.boolean(),
    /** Sound effects: mower, impacts, critters, buttons, coins. */
    sound: z.boolean(),
    /** The ambient bed (birdsong, breeze), separate from the effects. */
    ambience: z.boolean().default(true),
    reducedShake: z.boolean(),
    /** Swipe (default) or tap left/right halves (accessibility option). */
    controls: z.enum(['swipe', 'tap']).default('swipe'),
    /** Anonymous crash reports (opt-out; see public/privacy.html). */
    crashReports: z.boolean().default(true),
    /**
     * A daily notification when the Daily lawn is waiting (native apps). On by default; the phone
     * asks once after the first lawn, and a no turns it off (src/game/notifyAsk.ts).
     */
    dailyReminder: z.boolean().default(true),
    /**
     * Online scores (docs/ONLINE.md): best runs go to the leaderboards. Off: nothing is queued or
     * sent. Deleting the online data turns it off.
     */
    onlineScores: z.boolean().default(true),
    /** Friends see a dot while this player has the game open (docs/FRIENDS.md). */
    showOnline: z.boolean().default(true),
    /**
     * Friend alerts (docs/FRIENDS.md, step 4): push notifications for invites, friend requests and
     * a friend waiting in this player's room, while the app is closed. iPhone; on by default, like
     * the Daily reminder (the same ask).
     */
    friendAlerts: z.boolean().default(true),
  }),
  daily: z.object({
    /** Best clear time in ticks, per date key. */
    bestTicks: z.record(z.string(), z.number().int().positive()),
    streak: z.object({ lastDate: z.string().nullable(), count: z.number().int().min(0) }),
  }),
  /** Endless: `best` is Endless Lawns' best score; `bests` the other modes', by mode id. */
  endless: z.object({
    best: z.number().int().min(0),
    bests: z.record(z.string(), z.number().int().min(0)).default({}),
    /**
     * A run in progress, kept up to date lawn by lawn (mode, lawns so far, score and bank), so a
     * run cut short by the app closing still pays out: on the next visit to the menu.
     */
    openRun: z
      .object({
        mode: z.string(),
        stage: z.number().int().min(1),
        score: z.number().int().min(0),
        bank: z.array(z.object({ label: z.string(), coins: z.number().int() })),
      })
      .optional(),
  }),
  stats: z.object({
    lawnsMowed: z.number().int().min(0),
    tilesMowed: z.number().int().min(0),
  }),
  /**
   * Online play (docs/ONLINE.md): the anonymous player, once the game has joined, and runs waiting
   * to be sent. The server checks every run itself, so these are only shape-checked here (and the
   * types come from src/meta/online without importing it, which would be a cycle).
   */
  online: z
    .object({
      player: z
        .object({
          id: z.string(),
          token: z.string(),
          name: z.custom<PlayerName>((v) => typeof v === 'object' && v !== null),
        })
        .optional(),
      outbox: z
        .array(
          z.object({
            run: z.custom<PostedRun>((v) => typeof v === 'object' && v !== null),
            at: z.number().int(),
          }),
        )
        .default([]),
      /** The server asked for a newer game (HTTP 426): no posting until the game updates. */
      updateNeededFor: z.string().optional(),
      /**
       * The newest friend's answer to the player's challenges when they last looked at them (CLUB
       * > VERSUS): newer answers put a dot on the CLUB tab (src/meta/online/news.ts).
       */
      answersSeenAt: z.number().int().optional(),
      /**
       * Holding bonuses already paid (src/meta/online/awards.ts), by id, newest last: the server
       * answers the last week's again each time.
       */
      awardsPaid: z.array(z.string()).default([]),
      /**
       * This player's own nicknames for friends, by player id ("LIZA"): private, never sent to the
       * server (src/meta/online/nicknames.ts; read through `cleanNicknames`).
       */
      nicknames: z.record(z.string(), z.string()).optional(),
      /** The player's own secret friend code, once the server has made it (docs/FRIENDS.md). */
      friendCode: z.string().optional(),
      /** Friend alerts: `player:token` as the server last stored it (src/game/push.ts). */
      pushSent: z.string().optional(),
    })
    .default({ outbox: [], awardsPaid: [] }),
  /**
   * Live races (docs/LIVE.md): races and wins, and how many of today's (`day`, the local date) have
   * paid, since only the first few a day pay.
   */
  race: z
    .object({
      races: z.number().int().min(0).default(0),
      wins: z.number().int().min(0).default(0),
      day: z.string().default(''),
      paid: z.number().int().min(0).default(0),
    })
    .default({ races: 0, wins: 0, day: '', paid: 0 }),
});
export type Save = z.infer<typeof SaveSchema>;

/** A brand-new player. */
export function defaultSave(): Save {
  return {
    version: SAVE_VERSION,
    coins: 0,
    levels: {},
    ownedMowers: ['reel'],
    selectedMower: 'reel',
    upgrades: {},
    boosts: {},
    tools: { owned: [], levels: {}, equipped: [] },
    rareFinds: [],
    rareEscaped: [],
    rareWandering: [],
    zonesVisited: [],
    cosmetics: { owned: [], equipped: {} },
    zoneRewards: [],
    secretYards: [],
    customLawns: [],
    fullGame: false,
    builder: { owned: false, kits: [] },
    shares: 0,
    mowersDriven: [],
    settings: {
      haptics: true,
      sound: true,
      ambience: true,
      reducedShake: false,
      controls: 'swipe',
      crashReports: true,
      dailyReminder: true,
      onlineScores: true,
      showOnline: true,
      friendAlerts: true,
    },
    daily: { bestTicks: {}, streak: { lastDate: null, count: 0 } },
    endless: { best: 0, bests: {} },
    stats: { lawnsMowed: 0, tilesMowed: 0 },
    online: { outbox: [], awardsPaid: [] },
    race: { races: 0, wins: 0, day: '', paid: 0 },
  };
}
